/**
 * Realtime "sync needed" channel, like the official Todoist clients: open `user.websocket_url`, and
 * on `{"type":"sync_needed"}` run an incremental sync. The socket carries no data; the sync token
 * does the deduplication, so extra or lost notifications are harmless (the slow poll is the net).
 *
 * - Reconnects with exponential backoff and full jitter (1 s → 60 s cap), reset after a successful
 *   open; paused while the browser is offline, resumed immediately when it comes back.
 * - A ticket is only valid for a while (and dies with its token): when a connection fails before
 *   opening, or is closed as revoked (4001), the next attempt asks for a fresh URL.
 * - Sends `ping` every 30 s (answered `pong` by the server without waking it) to keep
 *   intermediaries from dropping an idle connection.
 * - After a reconnect, syncs once: notifications sent while disconnected were lost.
 */

export interface SocketLike {
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface RealtimeOptions {
  /** The websocket URL; `fresh` asks for a newly issued ticket (e.g. GET /user). null = none. */
  getUrl(fresh: boolean): Promise<string | null>;
  /** Called (debounced) when the server says a sync is needed. */
  onSyncNeeded(): void;
  /** Connected state changes (drives the fallback poll interval). */
  onConnectedChange?(connected: boolean): void;
  createSocket?(url: string): SocketLike;
  debounceMs?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  pingMs?: number;
  random?(): number;
}

export const CLOSE_REVOKED = 4001;

/** Backoff for the n-th consecutive failure (0-based): full jitter over [base·2ⁿ/2, base·2ⁿ], capped. */
export function backoffDelay(attempt: number, base: number, max: number, random: () => number): number {
  const ceiling = Math.min(max, base * 2 ** attempt);
  return Math.round(ceiling / 2 + (ceiling / 2) * random());
}

export class RealtimeClient {
  private socket: SocketLike | null = null;
  private running = false;
  private online = true;
  private attempts = 0;
  private needFreshUrl = false;
  private hasConnectedBefore = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  connected = false;

  private readonly o: Required<Omit<RealtimeOptions, "onConnectedChange">> & Pick<RealtimeOptions, "onConnectedChange">;

  constructor(options: RealtimeOptions) {
    this.o = {
      createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
      debounceMs: 250,
      baseDelayMs: 1000,
      maxDelayMs: 60_000,
      pingMs: 30_000,
      random: Math.random,
      ...options,
    };
  }

  start(online = true) {
    if (this.running) return;
    this.running = true;
    this.online = online;
    if (online) void this.connect();
  }

  stop() {
    this.running = false;
    this.clearTimers();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    const s = this.socket;
    this.socket = null;
    s?.close(1000, "client stopped");
    this.setConnected(false);
  }

  /** Browser online/offline events. Offline pauses reconnecting; online reconnects at once. */
  setOnline(online: boolean) {
    this.online = online;
    if (!this.running) return;
    if (!online) {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      return;
    }
    if (!this.socket && !this.retryTimer) {
      this.attempts = 0;
      void this.connect();
    }
  }

  private clearTimers() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.retryTimer = null;
    this.pingTimer = null;
  }

  private setConnected(connected: boolean) {
    if (this.connected === connected) return;
    this.connected = connected;
    this.o.onConnectedChange?.(connected);
  }

  private async connect() {
    if (!this.running || !this.online || this.socket) return;
    let url: string | null = null;
    try {
      url = await this.o.getUrl(this.needFreshUrl);
    } catch {
      url = null;
    }
    if (!this.running || !this.online || this.socket) return;
    if (!url) {
      this.needFreshUrl = true;
      this.scheduleRetry();
      return;
    }
    let opened = false;
    let socket: SocketLike;
    try {
      socket = this.o.createSocket(url);
    } catch {
      this.needFreshUrl = true;
      this.scheduleRetry();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      if (this.socket !== socket) return;
      opened = true;
      this.attempts = 0;
      this.needFreshUrl = false;
      this.setConnected(true);
      this.pingTimer = setInterval(() => {
        try {
          socket.send("ping");
        } catch {
          // closing
        }
      }, this.o.pingMs);
      if (this.hasConnectedBefore) this.syncNeeded();
      this.hasConnectedBefore = true;
    };
    socket.onmessage = (ev) => {
      if (this.socket !== socket || typeof ev.data !== "string" || ev.data === "pong") return;
      try {
        const msg = JSON.parse(ev.data) as { type?: string };
        if (msg.type === "sync_needed") this.syncNeeded();
      } catch {
        // not JSON: ignore
      }
    };
    socket.onerror = () => undefined;
    socket.onclose = (ev) => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.setConnected(false);
      // Rejected before opening (expired ticket?) or revoked: ask for a fresh URL next time.
      if (!opened || ev.code === CLOSE_REVOKED) this.needFreshUrl = true;
      this.scheduleRetry();
    };
  }

  private scheduleRetry() {
    if (!this.running || !this.online || this.retryTimer) return;
    const delay = backoffDelay(this.attempts, this.o.baseDelayMs, this.o.maxDelayMs, this.o.random);
    this.attempts++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, delay);
  }

  /** Trailing debounce: a burst of notifications becomes one sync. */
  private syncNeeded() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.running) this.o.onSyncNeeded();
    }, this.o.debounceMs);
  }
}
